import dayjs from "dayjs";
import { Badge, NumberFormatter } from "@/uiCore";
import { Stack } from "@altimateai/lego";
import { TooltipProps } from "recharts";
import { BarColors, TotalCostEntities } from "./constants";
import { getCostTypeLabel } from "./labels";
import { InfoIcon } from "@/assets/icons";
import classes from "../summary.module.scss";
import { useAppState } from "@/modules/app/useAppContext";

type Field = {
  key: { current: string; estimate: string };
  label: string;
  showChangePercentage?: boolean;
  hideSticker?: boolean;
  isTotal?: boolean;
  hideWhenZero?: boolean;
};

interface Props extends TooltipProps<number, string> {
  timeRange: "day" | "week" | "month";
  isFuture?: boolean;
  /** Resolves tooltip field config from Snowflake's TotalCostEntities. */
  type?: string;
  totalCostEntities?: ReturnType<typeof TotalCostEntities>;
  /** Explicit field list — overrides `type` lookup. */
  fields?: Field[];
  /** Field in the chart data row that holds the x-axis value (default `"date"`). */
  dateKey?: string;
  /** Overrides the default Snowflake BarColors lookup. */
  colorFor?: (key: string) => string;
  /**
   * Transforms the row label before rendering. Defaults to
   * `getCostTypeLabel`, which title-cases Snowflake cost-type strings.
   * Pass identity (`l => l`) when callers want labels rendered verbatim
   * (e.g. user-supplied Databricks job names).
   *
   * Named `formatFieldLabel` (not `labelFormatter`) because Recharts'
   * `TooltipProps` already declares `labelFormatter` with a different
   * signature `(label: any, payload: Payload[]) => ReactNode` — using
   * the same name produces a type clash when other Recharts consumers
   * pass that prop.
   */
  formatFieldLabel?: (label: string) => string;
}

const defaultColorFor = (key: string): string =>
  BarColors[key as keyof typeof BarColors] ?? "#8390A3";

const CostChartTooltip = ({
  payload,
  timeRange,
  type,
  isFuture,
  totalCostEntities = TotalCostEntities(),
  fields,
  dateKey = "date",
  colorFor = defaultColorFor,
  formatFieldLabel = getCostTypeLabel,
}: Props) => {
  const { currency } = useAppState();
  const data = payload && payload[0] && payload[0].payload;

  if (!data) {
    return null;
  }

  const resolvedFields: Field[] =
    fields ??
    (type ? (totalCostEntities[type as keyof typeof totalCostEntities]?.fields ?? []) : []);
  const totalObject = resolvedFields.find(item => item.isTotal);
  const otherFields = resolvedFields.filter(item => !item.isTotal);

  const totalKey = totalObject?.key["current"];

  const periodValue = data[dateKey];
  const isCurrentMonth = dayjs().isSame(dayjs(periodValue), "month");
  const isBeforeMonth = dayjs(periodValue).isBefore(dayjs(), "month");
  const isAfterMonth = dayjs(periodValue).isAfter(dayjs(), "month");

  const showCurrent = !isFuture || isBeforeMonth || isCurrentMonth;
  const showFuture = isFuture && (isCurrentMonth || isAfterMonth);

  const changePercent = (
    showChangePercentage: boolean,
    key: string,
    data: Record<string, string | number | null>
  ) => {
    if (!showChangePercentage) {
      return null;
    }
    const change = data[`percentage_${key}`];
    if (change == null) return null;
    const number = parseFloat(`${change}`);
    if (Number.isNaN(number)) return null;
    return (
      <span className={number >= 0 ? "negative" : "positive"}>
        ({number > 0 ? "+" : ""}
        {number.toFixed(2)}%)
      </span>
    );
  };

  const sticker = (field: Field, key: string) => {
    if (field.hideSticker) {
      return null;
    }
    return (
      <span
        className="al-inline-block al-h-1.5 al-w-2.5 al-shrink-0 al-rounded-[5px]"
        style={{ background: colorFor(key) }}
      />
    );
  };

  let hasChangePercentage = false;

  // For future estimates for current month, we need to add both current and estimate to get full estimated value
  const getEstimatedValue = (key: NonNullable<typeof totalObject>["key"]) => {
    if (isCurrentMonth) {
      return data[key["estimate"]] + data[key["current"]];
    }

    return data[key["estimate"]];
  };

  return (
    <div className={classes.costChartTooltip}>
      <h6>
        {dayjs(periodValue).format(timeRange === "month" ? "MMM YYYY" : "DD-MMM-YY")} (Aggregation:{" "}
        {timeRange})
      </h6>
      {totalKey ? (
        <Stack className="al-my-2">
          {showCurrent ? (
            <Badge
              tooltip=""
              className="al-gap-1 !al-bg-action-orange [&>.positive]:!al-text-white [&>.negative]:!al-text-white"
            >
              Total Cost:
              <NumberFormatter options={{ currency }} value={data[totalKey]} />
              {!isCurrentMonth ? changePercent(true, totalKey, data) : null}
            </Badge>
          ) : null}
          {showFuture ? (
            <Badge variant="secondary" tooltip="" className="al-gap-1">
              Projected:
              <NumberFormatter options={{ currency }} value={getEstimatedValue(totalObject.key)} />
              {changePercent(true, totalKey, data)}
            </Badge>
          ) : null}
        </Stack>
      ) : null}
      <Stack direction="column" gap="xs" className="al-my-1">
        {otherFields.map(field => {
          const key = field.key["current"];
          const currentValue = data[key];
          const estimateValue = data[field.key["estimate"]];
          const hasCurrentValue = currentValue !== null && currentValue !== undefined;
          const hasEstimateValue = estimateValue !== null && estimateValue !== undefined;

          // Hide breakdown row if all visible values are N/A
          if (showCurrent && !showFuture && !hasCurrentValue) return null;
          if (!showCurrent && showFuture && !hasEstimateValue) return null;
          if (showCurrent && showFuture && (!hasCurrentValue || !hasEstimateValue)) return null;

          // Backend returns 0 (not null) for categories a tenant doesn't
          // use. Hide the row when its only visible value is exactly 0.
          if (field.hideWhenZero) {
            const currentIsZero = !currentValue;
            const estimateIsZero = !estimateValue;
            if (showCurrent && !showFuture && currentIsZero) return null;
            if (!showCurrent && showFuture && estimateIsZero) return null;
            if (showCurrent && showFuture && currentIsZero && estimateIsZero) return null;
          }

          hasChangePercentage = hasChangePercentage || Boolean(field.showChangePercentage);
          return (
            <Stack key={key} align="center" className="al-gap-2 al-whitespace-nowrap">
              <Stack align="center" gap="xs" className="al-flex-1 al-pr-2">
                {isFuture && isCurrentMonth ? (
                  // Future current-month bars stack actual + estimate (two
                  // opacities); a single-color swatch would be misleading.
                  // Render an invisible spacer so the row indent stays aligned
                  // with rows that do show the sticker.
                  <span
                    aria-hidden="true"
                    className="al-inline-block al-h-1.5 al-w-2.5 al-shrink-0"
                  />
                ) : (
                  sticker(field, isFuture ? field.key["estimate"] : key)
                )}
                {formatFieldLabel(field.label)}:
              </Stack>
              {showCurrent ? (
                <div className="al-w-16 al-text-right">
                  <NumberFormatter showNaForNull options={{ currency }} value={data[key]} />
                </div>
              ) : null}
              {showFuture ? (
                <div className="al-w-16 al-text-right">
                  <NumberFormatter
                    showNaForNull
                    options={{ currency }}
                    value={data[field.key["estimate"]]}
                  />
                </div>
              ) : null}
              {field.showChangePercentage ? (
                <div className="al-w-16 al-text-right">{changePercent(true, key, data)}</div>
              ) : null}
            </Stack>
          );
        })}
      </Stack>
      {hasChangePercentage && timeRange ? (
        <div style={{ background: "#F5FAFF", padding: 8 }}>
          <Stack style={{ maxWidth: 215 }}>
            <InfoIcon className="al-w-6 al-h-4" /> Change is calculated from the value for the
            previous {timeRange}
          </Stack>
        </div>
      ) : null}
    </div>
  );
};

export default CostChartTooltip;
